import React from "react";
import { ArrowRight, Sparkles, Leaf, Volume2, VolumeX } from "lucide-react";
import { useSiteContent, getSection } from "../../context/SiteContentContext";

const FALLBACK = {
    badge: "Naturalmente Premium",
    chip_text: "Pronto em 30s",
    title_pre: "A pureza da fruta,",
    title_highlight: "preservada",
    title_suffix: "a -18 °C.",
    description:
        "Polpas de fruta congeladas no auge da maturação. O sabor no seu estado mais puro — sem conservantes, sem desperdício, pronto em segundos.",
    cta_primary_label: "Explorar a Coleção",
    cta_primary_href: "#produtos",
    cta_secondary_label: "Seja um Parceiro",
    cta_secondary_href: "#oportunidade",
    hero_video: "/videos/video-hero.mp4",
    hero_image:
        "https://images.unsplash.com/photo-1624950240173-b651b9e40188?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    stat_top_n: "100%",
    stat_top_l: "fruta",
    stat_mid_n: "Sem",
    stat_mid_l: "conservantes",
    stat_bot_n: "30s",
    stat_bot_l: "preparação",
};

export const Hero = () => {
    const videoRef = React.useRef(null);
    const [muted, setMuted] = React.useState(true);

    const { content } = useSiteContent();
    const h = { ...FALLBACK, ...getSection(content, "hero", {}) };

    const toggleSound = () => {
        const v = videoRef.current;
        if (!v) return;
        v.muted = !v.muted;
        setMuted(v.muted);
    };

    const isVideo = h.hero_video && /\.(mp4|webm|ogg)(\?|$)/i.test(h.hero_video);

    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative overflow-hidden pt-12 pb-24 md:pb-32 lg:pt-20 lg:pb-40"
        >
            <div
                aria-hidden
                className="absolute inset-0 opacity-[0.06] bg-hex-pattern pointer-events-none"
                style={{ backgroundImage: "url('/brand/pattern-1.png')" }}
            />
            <div
                aria-hidden
                className="absolute -top-32 -right-32 h-[480px] w-[480px] rounded-full bg-brand-orange/30 blur-3xl pointer-events-none"
            />
            <div
                aria-hidden
                className="absolute bottom-0 -left-40 h-[420px] w-[420px] rounded-full bg-brand-green/25 blur-3xl pointer-events-none"
            />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
                <div className="lg:col-span-7 animate-fade-up">
                    <span className="inline-flex items-center gap-2 rounded-full border border-brand-green/20 bg-white/70 backdrop-blur px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-brand-green">
                        <Sparkles className="h-3.5 w-3.5" />
                        {h.badge}
                    </span>

                    <h1 className="mt-6 font-display font-medium text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-brand-ink">
                        {h.title_pre}{" "}
                        <span className="relative inline-block">
                            <span className="relative z-10 italic text-brand-orange">
                                {h.title_highlight}
                            </span>
                        </span>{" "}
                        <span className="text-brand-ink">{h.title_suffix}</span>
                    </h1>

                    <p className="mt-6 text-lg sm:text-xl text-brand-ink-soft max-w-2xl leading-relaxed">
                        {h.description}
                    </p>

                    <div className="mt-10 flex flex-col sm:flex-row gap-3">
                        <a
                            href={h.cta_primary_href || "#produtos"}
                            data-testid="hero-cta-experimentar"
                            className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand-ink hover:bg-[#2C312E] text-brand-cream px-7 py-4 text-base font-semibold transition-all shadow-pop hover:translate-y-[-2px]"
                        >
                            {h.cta_primary_label}
                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </a>
                        <a
                            href={h.cta_secondary_href || "#oportunidade"}
                            data-testid="hero-cta-parceiro"
                            className="group inline-flex items-center justify-center gap-2 rounded-full bg-white border-2 border-brand-ink/10 hover:border-brand-orange text-brand-ink px-7 py-4 text-base font-semibold transition-all hover:translate-y-[-2px]"
                        >
                            {h.cta_secondary_label}
                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </a>
                    </div>

                    <div className="mt-10 flex items-center gap-6 text-sm text-brand-ink-soft">
                        <div className="flex items-center gap-2" data-testid="hero-stat-top">
                            <Leaf className="h-4 w-4 text-brand-green" />
                            <span className="font-semibold">{h.stat_top_n}</span>
                            <span>{h.stat_top_l}</span>
                        </div>
                        <div
                            className="hidden sm:flex items-center gap-2"
                            data-testid="hero-stat-mid"
                        >
                            <span className="h-1.5 w-1.5 rounded-full bg-brand-orange" />
                            <span className="font-semibold">{h.stat_mid_n}</span>
                            <span>{h.stat_mid_l}</span>
                        </div>
                        <div
                            className="hidden sm:flex items-center gap-2"
                            data-testid="hero-stat-bot"
                        >
                            <span className="h-1.5 w-1.5 rounded-full bg-brand-green" />
                            <span className="font-semibold">{h.stat_bot_n}</span>
                            <span>{h.stat_bot_l}</span>
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-5 relative">
                    <div className="relative animate-float">
                        <div className="absolute inset-0 bg-brand-orange/30 blur-3xl rounded-full" />
                        <div className="relative rounded-2xl overflow-hidden shadow-pop border-8 border-white">
                            {isVideo ? (
                                <>
                                    <video
                                        ref={videoRef}
                                        src={h.hero_video}
                                        poster={h.hero_image || FALLBACK.hero_image}
                                        autoPlay
                                        loop
                                        muted
                                        playsInline
                                        preload="auto"
                                        data-testid="hero-video"
                                        className="w-full h-[460px] lg:h-[560px] object-cover bg-brand-orange/20"
                                    />
                                    <button
                                        type="button"
                                        onClick={toggleSound}
                                        data-testid="hero-video-sound"
                                        aria-label={muted ? "Ativar som" : "Desativar som"}
                                        className="absolute top-4 right-4 h-10 w-10 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur text-white flex items-center justify-center transition-all"
                                    >
                                        {muted ? (
                                            <VolumeX className="h-4 w-4" />
                                        ) : (
                                            <Volume2 className="h-4 w-4" />
                                        )}
                                    </button>
                                </>
                            ) : (
                                <img
                                    src={h.hero_image || FALLBACK.hero_image}
                                    alt="Hero"
                                    data-testid="hero-image"
                                    className="w-full h-[460px] lg:h-[560px] object-cover bg-brand-orange/20"
                                />
                            )}
                        </div>
                        <div className="absolute -bottom-6 -left-4 bg-white rounded-2xl p-4 shadow-soft flex items-center gap-3 border border-brand-green/10">
                            <div className="h-12 w-12 rounded-full bg-brand-green-soft flex items-center justify-center">
                                <Leaf className="h-5 w-5 text-brand-green" />
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-widest text-brand-orange font-bold">
                                    Congelada
                                </div>
                                <div className="text-sm font-bold text-brand-ink">
                                    Fruta conservada a -18 °C
                                </div>
                            </div>
                        </div>
                        {h.chip_text && (
                            <div className="hidden md:flex absolute -top-6 -right-6 bg-brand-orange rounded-2xl px-4 py-3 shadow-pop text-white items-center gap-2 rotate-3">
                                <Sparkles className="h-4 w-4" />
                                <span className="font-bold text-sm">{h.chip_text}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};
